import { Link, router, usePage } from "@inertiajs/react"
import { cn } from "cn"
import { ChevronDown, Menu, X } from "lucide-react"
import { useState } from "react"

import iconUrl from "@/assets/organobooks-icon.svg"
import userPlaceholderUrl from "@/assets/user_placeholder.png"
import { AccountSwitcher } from "@/components/account-switcher"
import { ActivityPanel } from "@/components/activity-panel"
import { ClientSwitcher } from "@/components/client-switcher"
import { CompanySwitcher } from "@/components/company-switcher"
import { FeedbackDialog } from "@/components/feedback-dialog"
import { AdminViewBadge } from "@/components/read-only-session"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  frontAccountantClients,
  frontAccountantDeadlines,
  frontAccountantFirms,
  frontAccountantProfiles,
  frontAccounts,
  frontBankAccounts,
  frontBankStatementFiles,
  frontCompanies,
  frontCounterparties,
  frontDocuments,
  frontIncomingEmailAllowedSenders,
  frontIncomingEmails,
  frontInvoices,
  frontLedgerAccounts,
  frontLedgerBalanceSheets,
  frontLedgerCounterparties,
  frontLedgerDocuments,
  frontLedgerJournalEntries,
  frontLedgerManualJournals,
  frontLedgerPayments,
  frontLedgerPeriods,
  frontLedgerProfitAndLosses,
  frontLedgerReviewQueues,
  frontLedgerTrialBalances,
  frontOverviewMonthEnds,
  frontOverviewYears,
  frontPaymentMatchingRules,
  frontPayments,
  frontProducts,
  frontPurchaseOrders,
  frontRecurringInvoices,
  frontSalesDocuments,
  frontSettings,
  frontUsersSessions,
} from "@/routes"

/** `exact`: the item is current on its own page only, not on the pages below it. */
type NavItem = { title: string; href: string; exact?: boolean }

const navItemClasses = "rounded-md px-3 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-inverse-foreground"
const activeClasses = "bg-inverse-active text-inverse-foreground"
const inactiveClasses = "text-inverse-muted-foreground hover:bg-inverse-hover hover:text-inverse-foreground"

function isActive(currentPath: string, href: string, exact = false) {
  return currentPath === href || (!exact && currentPath.startsWith(`${href}/`))
}

function NavLink({ item, currentPath }: { item: NavItem; currentPath: string }) {
  const active = isActive(currentPath, item.href, item.exact)

  return (
    <Link
      href={item.href}
      aria-current={active ? "page" : undefined}
      className={cn(navItemClasses, active ? activeClasses : inactiveClasses)}
    >
      {item.title}
    </Link>
  )
}

function NavMenuItems({ items, currentPath }: { items: NavItem[]; currentPath: string }) {
  return items.map((item) => (
    <DropdownMenuItem key={item.href} asChild className={cn(isActive(currentPath, item.href) && "bg-accent")}>
      <Link href={item.href}>{item.title}</Link>
    </DropdownMenuItem>
  ))
}

/** A link with an attached menu of related pages. */
function NavSplitLink({
  item,
  items,
  menuLabel,
  currentPath,
}: {
  item: NavItem
  items: NavItem[]
  menuLabel: string
  currentPath: string
}) {
  const active = isActive(currentPath, item.href)

  return (
    <div className="inline-flex rounded-md">
      <Link
        href={item.href}
        aria-current={active ? "page" : undefined}
        className={cn(navItemClasses, "rounded-r-none font-semibold", active ? activeClasses : inactiveClasses)}
      >
        {item.title}
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={menuLabel}
          className={cn("rounded-r-md px-2 py-2 outline-none focus-visible:ring-2 focus-visible:ring-inverse-foreground", active ? activeClasses : inactiveClasses)}
        >
          <ChevronDown className="size-5" aria-hidden="true" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <NavMenuItems items={items} currentPath={currentPath} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function NavMenu({ title, items, currentPath }: { title: string; items: NavItem[]; currentPath: string }) {
  const active = items.some((item) => isActive(currentPath, item.href))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          navItemClasses,
          "inline-flex items-center gap-1.5 font-semibold",
          active ? activeClasses : inactiveClasses,
        )}
      >
        {title}
        <ChevronDown className="-mr-1 size-5" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <NavMenuItems items={items} currentPath={currentPath} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** The person's own pages: the company's account and settings, or the accountant's profile and firm. */
function UserMenu({ accounting }: { accounting: boolean }) {
  // During an admin view, signing out would end the admin session too; the badge exits the view.
  const adminView = usePage().props.admin_view

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Open user menu"
        className="ml-3 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-inverse-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-inverse"
      >
        <img src={userPlaceholderUrl} alt="" className="size-8 rounded-full" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {accounting ? (
          <>
            <DropdownMenuItem asChild>
              <Link href={frontAccountantProfiles.show().url}>Your profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href={frontAccountantFirms.show().url}>Firm</Link>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem asChild>
              <Link href={frontAccounts.edit().url}>Account</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href={frontSettings.edit().url}>Settings</Link>
            </DropdownMenuItem>
          </>
        )}
        {!adminView && (
          <DropdownMenuItem onSelect={() => router.delete(frontUsersSessions.destroy().url)}>Logout</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

type NavSection = { item: NavItem; pages: NavItem[] }

/** The ledger's pages, behind the accountant's "Ledger" item. */
function ledgerPages(): NavItem[] {
  return [
    { title: "Trial balance", href: frontLedgerTrialBalances.show().url },
    { title: "Profit and loss", href: frontLedgerProfitAndLosses.show().url },
    { title: "Balance sheet", href: frontLedgerBalanceSheets.show().url },
    { title: "Journal", href: frontLedgerJournalEntries.index().url },
    { title: "Manual journals", href: frontLedgerManualJournals.index().url },
    { title: "Chart of accounts", href: frontLedgerAccounts.index().url },
  ]
}

/** The accounting app's pages of a client's records, behind the accountant's "More" item. */
function recordPages(): NavItem[] {
  return [
    { title: "Documents", href: frontLedgerDocuments.index().url },
    { title: "Transactions", href: frontLedgerPayments.index().url },
    { title: "Counterparties", href: frontLedgerCounterparties.index().url },
  ]
}

/**
 * The accounting app's navigation: the portal across
 * clients, then, after the client switcher, the client's overview, the review queue, the returns,
 * the ledger and records. A month's review opens from the overview's month chips. Nothing of the company app; the firm is in the user menu.
 */
function accountantSections(clientId: number | null): { portal: NavItem[]; client: NavSection[] } {
  return {
    portal: [
      { title: "Clients", href: frontAccountantClients.index().url, exact: true },
      { title: "Deadlines", href: frontAccountantDeadlines.index().url },
    ],
    client: clientId
      ? [
          { item: { title: "Overview", href: frontAccountantClients.show(clientId).url }, pages: [] },
          { item: { title: "Review", href: frontLedgerReviewQueues.show().url }, pages: [] },
          { item: { title: "Returns", href: frontLedgerPeriods.index().url }, pages: [] },
          { item: { title: "Ledger", href: frontLedgerTrialBalances.show().url }, pages: ledgerPages() },
          { item: { title: "More", href: frontLedgerDocuments.index().url }, pages: recordPages() },
        ]
      : [],
  }
}

export function AppNavbar() {
  const page = usePage()
  const navigation = page.props.navigation
  const accountantNavigation = page.props.accountant_navigation
  const [mobileOpen, setMobileOpen] = useState(false)

  if (!navigation) return null

  const currentPath = new URL(page.url, window.location.origin).pathname
  const { current_year: currentYear, years } = navigation

  // The session's account decides the app: an accountant's
  // shows the accounting app's pages, any other the company app's. The switchers change the account.
  const match: NavItem = { title: "Match", href: frontOverviewMonthEnds.show().url }
  const transactions: NavItem = { title: "Transactions", href: frontPayments.index().url }
  const documentsItem: NavItem = { title: "Documents", href: frontDocuments.index().url }
  const invoicesItem: NavItem = { title: "Invoices", href: frontInvoices.index().url }
  const overview: NavItem = { title: `${currentYear} Overview`, href: frontOverviewYears.show(currentYear).url }
  // The pages behind each section's arrow; the mobile menu lists them under the section.
  const transactionPages: NavItem[] = [
    { title: "Bank Accounts", href: frontBankAccounts.index().url },
    { title: "Bank Statement Uploads", href: frontBankStatementFiles.index().url },
  ]
  const documentPages: NavItem[] = [{ title: "Purchase orders", href: frontPurchaseOrders.index().url }]
  const invoicePages: NavItem[] = [
    { title: "Recurring invoices", href: frontRecurringInvoices.index().url },
    { title: "Sales documents", href: frontSalesDocuments.index().url },
    { title: "Products", href: frontProducts.index().url },
  ]
  const accountant = accountantNavigation ? accountantSections(accountantNavigation.client_id) : null
  const mobileSections: NavSection[] = accountant ? [...accountant.portal.map((item) => ({ item, pages: [] })), ...accountant.client] : [
    { item: match, pages: [] },
    { item: transactions, pages: transactionPages },
    { item: documentsItem, pages: documentPages },
    { item: invoicesItem, pages: invoicePages },
    {
      item: overview,
      pages: years.filter((year) => year !== currentYear).map((year) => ({ title: `${year} Overview`, href: frontOverviewYears.show(year).url })),
    },
  ]
  const home = accountant ? frontAccountantClients.index().url : match.href

  return (
    <nav className="fixed top-0 z-50 w-full bg-inverse">
      <div className="mx-auto px-2 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between">
          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
            <button
              type="button"
              aria-controls="mobile-menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((open) => !open)}
              className="inline-flex items-center justify-center rounded-md p-2 text-inverse-muted-foreground hover:bg-inverse-hover hover:text-inverse-foreground focus-visible:ring-2 focus-visible:ring-inverse-foreground focus-visible:outline-none focus-visible:ring-inset"
            >
              <span className="sr-only">{mobileOpen ? "Close main menu" : "Open main menu"}</span>
              {mobileOpen ? <X className="size-6" aria-hidden="true" /> : <Menu className="size-6" aria-hidden="true" />}
            </button>
          </div>

          <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
            <Link href={home} className="flex shrink-0 items-center">
              <img src={iconUrl} alt="OrganoBooks" className="size-10" />
            </Link>
            {accountant ? (
              // On a phone the client switcher stays beside the logo; the menu holds the rest.
              <div className="ml-2 flex items-center sm:hidden">
                <ClientSwitcher />
              </div>
            ) : (
              <div className="ml-2 flex items-center gap-1 sm:ml-3">
                <AccountSwitcher />
                <CompanySwitcher />
              </div>
            )}
            <div className="hidden items-center gap-3 sm:ml-6 sm:flex">
              {accountant ? (
                <>
                  {accountant.portal.map((item) => (
                    <NavLink key={item.href} item={item} currentPath={currentPath} />
                  ))}
                  <span className="h-6 w-px bg-inverse-hover" aria-hidden="true" />
                  <ClientSwitcher />
                  {accountant.client.map(({ item, pages }) =>
                    pages.length > 0 ? (
                      <NavMenu key={item.title} title={item.title} currentPath={currentPath} items={pages} />
                    ) : (
                      <NavLink key={item.href} item={item} currentPath={currentPath} />
                    ),
                  )}
                </>
              ) : (
                <>
                  <NavLink item={match} currentPath={currentPath} />
                  <NavSplitLink
                    item={transactions}
                    menuLabel="Transaction pages"
                    currentPath={currentPath}
                    items={transactionPages}
                  />
                  <NavSplitLink
                    item={documentsItem}
                    menuLabel="Document pages"
                    currentPath={currentPath}
                    items={documentPages}
                  />
                  <NavSplitLink
                    item={invoicesItem}
                    menuLabel="Invoice pages"
                    currentPath={currentPath}
                    items={invoicePages}
                  />
                  <NavSplitLink
                    item={overview}
                    menuLabel="Year overview options"
                    currentPath={currentPath}
                    items={years
                      .filter((year) => year !== currentYear)
                      .map((year) => ({ title: String(year), href: frontOverviewYears.show(year).url }))}
                  />
                  <NavMenu
                    title="Misc"
                    currentPath={currentPath}
                    items={[
                      { title: "Matching Rules", href: frontPaymentMatchingRules.index().url },
                      { title: "Counterparties", href: frontCounterparties.index().url },
                      { title: "Incoming Emails", href: frontIncomingEmails.index().url },
                      { title: "Allowed Senders", href: frontIncomingEmailAllowedSenders.index().url },
                      { title: "Companies", href: frontCompanies.index().url },
                    ]}
                  />
                </>
              )}
            </div>
          </div>

          <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            <AdminViewBadge />
            <FeedbackDialog pageContext={currentPath} />
            {/* The company's activity: its syncs, exports and the accountant's notes to it. */}
            {!accountant && <ActivityPanel />}
            <UserMenu accounting={accountant !== null} />
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div id="mobile-menu" className="fixed inset-x-0 top-16 bottom-0 overflow-y-auto bg-inverse sm:hidden">
          <div className="flex flex-col gap-1 px-4 py-6">
            {mobileSections.map(({ item, pages }) => (
              <div key={item.href} className="flex flex-col gap-1">
                <Link
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "block w-full rounded-md px-4 py-3 text-xl font-medium",
                    isActive(currentPath, item.href, item.exact) ? activeClasses : inactiveClasses,
                  )}
                >
                  {item.title}
                </Link>
                {pages.map((subpage) => (
                  <Link
                    key={subpage.href}
                    href={subpage.href}
                    onClick={() => setMobileOpen(false)}
                    className={cn("block w-full rounded-md py-2 pr-4 pl-8 text-base font-medium", isActive(currentPath, subpage.href) ? activeClasses : inactiveClasses)}
                  >
                    {subpage.title}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  )
}
